import { StudyStoreRegister } from '@/domain/study-store-register';
import { StudyStoreState } from '@/enums/study-store-state';
import { WarningMessages } from '@/enums/warning-messages';
import { RenderOverviewAICommand, WidgetTypes } from 'interfaces';
import Banner from '@/presentation/components/widget/Banner/Banner.vue';
import ButtonContentSwitcher from '@/presentation/components/widget/ButtonContentSwitcher/ButtonContentSwitcher.vue';
import ButtonEssentialInfo from '@/presentation/components/widget/ButtonEssentialInfo/ButtonEssentialInfo.vue';
import ButtonHeader from '@/presentation/components/widget/ButtonHeader/ButtonHeader.vue';
import ButtonIcon from '@/presentation/components/widget/ButtonIcon/ButtonIcon.vue';
import OmniSearchButtonIcon from '@/presentation/components/widget/OmniSearchButtonIcon/OmniSearchButtonIcon.vue';
import ButtonText from '@/presentation/components/widget/ButtonText/ButtonText.vue';
import MainDeterminant from '@/presentation/components/widget/MainDeterminant/MainDeterminant.vue';
import { store } from '@/store/store';
import { GetFitByDeterminantCommand } from 'interfaces';
import { IBestfitOverviewApplication } from 'interfaces/bestfit-overview.interface';
import { FitLevel } from 'interfaces';
import { createApp, h } from 'vue';
import SubDeterminantBadge from '@/presentation/components/widget/SubDeterminantBadge/SubDeterminantBadge.vue';
import {
	AdvicePresentation,
	IAdvice,
} from '@studyportals/bestfit-api-fe-client';
import App from '@/presentation/components/App.vue';
import GlobalPlugin from '@/vue-globals';

export default class BestfitOverviewApplication
implements IBestfitOverviewApplication {
	private StudyStoreRegister: StudyStoreRegister;

	private widgetComponents = {
		[WidgetTypes.BANNER]: Banner,
		[WidgetTypes.BUTTON_TEXT]: ButtonText,
		[WidgetTypes.BUTTON_ICON]: ButtonIcon,
		[WidgetTypes.OMNI_SEARCH_BUTTON_ICON]: OmniSearchButtonIcon,
		[WidgetTypes.BUTTON_HEADER]: ButtonHeader,
		[WidgetTypes.BUTTON_CONTENT_SWITCHER]: ButtonContentSwitcher,
		[WidgetTypes.BUTTON_ESSENTIAL_INFO]: ButtonEssentialInfo,
		[WidgetTypes.MAIN_DETERMINANT]: MainDeterminant,
		[WidgetTypes.SUB_DETERMINANT_BADGE]: SubDeterminantBadge,
	};

	constructor() {
		this.StudyStoreRegister = new StudyStoreRegister();
	}

	public hasCompletedBestfit(): boolean {
		const completeness = store.getters.bestFitCompleteness();

		return completeness ? completeness === 100 : false;
	}

	public async getFitByDeterminant(
		command: GetFitByDeterminantCommand,
	): Promise<FitLevel | null> {
		let studyStore = store.getters.getStudyStore(command.studyId);

		if (!studyStore) {
			this.StudyStoreRegister.registerStudyStore(command.studyId);

			await this.startFitCalculation();

			studyStore = store.getters.getStudyStore(command.studyId);
		}

		const score = studyStore?.getters.scoreByDeterminant(
			command.scoreDeterminantType,
		);

		if (score === null || score === undefined) {
			return null;
		}

		const scoreRouned = Math.round(score / 10) * 10;

		return store.getters.scoreText(scoreRouned);
	}

	public async getAdviceByDeterminant(
		command: GetFitByDeterminantCommand,
	): Promise<IAdvice<AdvicePresentation, AdvicePresentation> | null> {
		let studyStore = store.getters.getStudyStore(command.studyId);
		if (!studyStore) {
			this.StudyStoreRegister.registerStudyStore(command.studyId);

			await this.startFitCalculation();

			studyStore = store.getters.getStudyStore(command.studyId);
		}

		const advice = studyStore?.getters.adviceByDeterminant(
			command.scoreDeterminantType,
		);
		return advice ?? null;
	}

	public async renderOverviewAI(command: RenderOverviewAICommand): Promise<void> {
		const { detail } = command;
		const isOverviewAI = detail.overviewAI;
		const studyId = detail.studyId;

		if (!studyId) {
			console.warn(WarningMessages.INVALID_STUDY_ID);
			return;
		}

		this.StudyStoreRegister.registerStudyStore(studyId);

		store.mutations.setActiveStudyId(studyId);

		const overviewAI = createApp({
			render: () => {
				return h(App, {
					isOverviewAI,
					studyId
				});
			},
		});
		overviewAI.use(GlobalPlugin);
		overviewAI.mount(detail.element);

		await this.startFitCalculation();
	}

	public async renderWidgets(): Promise<void> {
		const nodes: NodeListOf<Element> =
			document.querySelectorAll('.BestFitOverview');

		if (nodes.length < 1) {
			console.warn(WarningMessages.NO_VALID_PLACEHOLDERS);
			return;
		}

		nodes.forEach((node) => {
			this.mountWidget(node);
		});

		await this.startFitCalculation();
	}
	private mountWidget(node: Element): void {
		const studyId = parseInt(
			node.getAttribute('data-study-id') as string,
			10,
		);
		const widgetType = node.getAttribute('data-widget-type') as WidgetTypes;
		const widgetLocation = node.getAttribute(
			'data-widget-location',
		) as string;
		const emptyText = node.getAttribute('data-empty-text') as string;
		const determinantType = parseInt(
			node.getAttribute('data-determinant-type') as string,
			10,
		);

		if (!studyId) {
			console.warn(WarningMessages.INVALID_STUDY_ID);
			return;
		}

		if (!this.isValidWidgetType(widgetType)) {
			console.warn(`${WarningMessages.INVALID_WIDGET_TYPE}: ${widgetType}`);
			return;
		}

		this.StudyStoreRegister.registerStudyStore(studyId);

		const widget = createApp({
			render: () => {
				return h(this.widgetComponents[widgetType] as string, {
					studyId,
					widgetType,
					widgetLocation,
					emptyText,
					determinantType,
				});
			},
		});
		widget.use(GlobalPlugin);
		widget.mount(node);
	}

	private async startFitCalculation(): Promise<void> {
		store.actions.convertStudyStoreState({
			from: StudyStoreState.CREATED,
			to: StudyStoreState.PENDING,
		});
		await store.actions.calculateFit();
	}

	private isValidWidgetType(widgetType: WidgetTypes): boolean {
		if (Object.values(WidgetTypes).includes(widgetType)) {
			return true;
		}

		return false;
	}
}
